// src/forms/data_hub.js
import {
    return_valid_or_default_forms_domain,
    inject_styles
} from '../helpers/index';

import { addDataHubMessageListener } from '../forms/cors_iframe';
import { bind_all_tel_inputs_intl } from '../tel/telephone_sms_inputs';
import defaultsDeep from 'lodash.defaultsdeep';
import { load_user_data_from_jwt_url_params } from '../helpers/get_process_user_data';
import { merge_user_data_browser } from '../helpers/user_data_smart_merge';
import { get_most_parent_url_params } from '../helpers/parse_url_params';
import { default_forms_domain, default_data_hub_css_url, default_organization_id, default_theme, build_organization_metadata } from '../helpers/static_vars';
import { show_error_popover } from './html_element_helpers/error_popover';
import { log_console_error_and_track_to_action_endpoint_with_rate_limit } from './html_element_helpers/error_tracking';
import { inject_data_hub_css_link, run_theme_json_to_base64 } from './html_element_helpers/form_styling';
import {
    add_form_input_loading_state,
    remove_form_input_loading_state,
    add_form_loading_overlay,
    remove_form_loading_overlay,
    prefill_form
} from '../forms/shared_form_ux_functions';
import { updateActblueConfigWithDatahub, init_actblue } from '../helpers/actblue_integration';
import { normalize_data_hub_organization_id_string } from '../helpers/action_request_helpers';
import { normalize_data_hub_privacy_policy_string } from '../helpers/data_hub_privacy_policy';
import { v4 as uuidv4 } from 'uuid';

// Standalone functions that take data_hub_instance as first param.
// Aliased so class methods can share the same names without recursion, and JSDoc {@link …} jumps here then into the module.
import {
    load_organization_public_metadata as load_organization_public_metadata_from_metadata_module,
    set_page_metadata_defaults as set_page_metadata_defaults_from_metadata_module,
    organization_metadata_is_complete_enough_to_skip_fetch,
    return_organization_metadata
} from './data_hub_metadata';
import { got_cors_user_data_cache as got_cors_user_data_cache_from_cors_user_data_module } from './data_hub_cors_user_data';
import {
    got_clear_jwt_response as got_clear_jwt_response_from_forget_user_module,
    forget_user as forget_user_from_forget_user_module
} from './data_hub_forget_user';
import { init_analytics_bootstrap } from '../analytics/bootstrap';

export class data_hub_class {
    constructor(config = {}, callback_function_after_init = () => { }) {
        // Multi-instance is unsupported. The analytics pushState/replaceState wrappers bind
        // to whichever instance constructs first, <data-hub-signup-form> reads window.data_hub
        // so all forms feed one instance, and identity / user_data fetches run twice. Warn
        // instead of throwing so existing call sites that race the form custom element keep
        // working — the second construction will mostly mirror the first, but with subtle
        // ordering bugs around analytics and ActBlue refcodes.
        if (typeof window !== 'undefined' && window.data_hub && window.data_hub.is_initialized) {
            console.warn(
                'data_hub_class: a DataHub instance already exists on window.data_hub. '
                + 'Multi-instance is not supported — auto-event listeners and SPA history '
                + 'wrappers bind to the first instance only. Reuse window.data_hub or call '
                + 'window.data_hub.forget_user() instead of constructing a second instance.'
            );
        }

        const default_error_function = (internal_error_message, external_error_message) => {
            log_console_error_and_track_to_action_endpoint_with_rate_limit({
                internal_error_message,
                external_error_message,
                data_hub_instance: this,
                user_data: this.user_data || {},
                metadata: this.page_metadata || {},
                maximum_error_tracking_requests_per_page: 3
            });
            show_error_popover(external_error_message, this.target || document);
        };
        this.error_function = config.error_function || default_error_function;

        // Set iframe cors listener passing the reference
        addDataHubMessageListener(this);

        // Start ActBlue integration (load its configuration)
        this.has_init_actblue_merged_configuration = false;
        // Errors are handled inside init_actblue via error_function; promise resolves after catch.
        init_actblue(this.error_function);

        // organization_metadata cascade: explicit config > window.data_hub_organization_metadata (injected by landing page) > build_organization_metadata (Mode B baked-in blob) > {}
        this.organization_metadata = return_organization_metadata(
            config,
            typeof window !== 'undefined' ? window : null,
            build_organization_metadata
        );

        // Resolution chain: explicit config > organization_metadata > build default (org-specific builds only, null for shared).
        // normalize_data_hub_organization_id_string returns null for empty/non-UUID values.
        this.organization_id =
            normalize_data_hub_organization_id_string(config?.organization_id)
            || normalize_data_hub_organization_id_string(this.organization_metadata?.organization_id)
            || normalize_data_hub_organization_id_string(default_organization_id)
            || null;

        const forms_domain_from_config = config.data_hub_action_domain || config.forms_domain
            || this.organization_metadata.data_hub_action_domain || this.organization_metadata.forms_domain;
        this.forms_domain = return_valid_or_default_forms_domain(forms_domain_from_config || default_forms_domain);
        this.url_params = get_most_parent_url_params(this.error_function);
        this.iterableCampaignId = null;
        this.iterableTemplateId = null;

        // Local set for prefilled forms
        this.prefilled_forms = new Set();

        // ID to compare uniqueness of data_hub inits
        this.data_hub_init_id = uuidv4();

        // Global shared user_data object
        this.user_data = (config.user_data && typeof config.user_data === 'object') ? config.user_data : {};
        this.jwt_cookie = null;

		this.privacy_policy = normalize_data_hub_privacy_policy_string(config.privacy_policy);

        this.actblue_token = config.actblue_token || null;

        this.actblue_amounts = config.actblue_amounts || [5,10,20,50,100];
        this.actblue_default_page_url = config.actblue_default_page_url || null;
        this.actblue_preview = config.actblue_preview || false;
        this.actblue_css_url = config.actblue_css_url || 'https://qtera.org/css/actblue_modal_600_max.css';

        this.bind_tel_inputs_intl = config.bind_tel_inputs_intl || false;

        // Setup promise to signal when user data is loaded
        this.user_data_loaded = false;
        this.user_data_loaded_promise = new Promise((resolve, reject) => {
            this.resolve_user_data_loaded = () => {
                this.user_data_loaded = true;
                return resolve();
            };
            this.reject_user_data_loaded = (error) => {
                this.user_data_loaded = false;
                return reject(error);
            };
        }).then(async () => {
            this.user_data = await merge_user_data_browser(
                window.data_hub_user_data || {},
                this.user_data || {}
            );

            updateActblueConfigWithDatahub(this);

            document.dispatchEvent(new CustomEvent('data-hub-new-user-data', {
                detail: {
                    user_data: this.user_data || {},
                    prefill_input_selector: this.prefill_input_selector || null,
                    data_hub_instance: this,
                    target: this.target || document
                },
                bubbles: true,
                composed: true
            }));

            if (this.bind_tel_inputs_intl) {
                this.iti_inputs = await bind_all_tel_inputs_intl(
                    this.target || document,
                    this
                );
            }

        })
            .catch((error) => {
                this.error_function?.('Error in user_data_loaded_promise: ' + error, null);
            })

        // Create page metadata and load Iterable IDs
        set_page_metadata_defaults_from_metadata_module(this, config);

        // Prefill settings
        this.target = config.target || null;
        this.style_target = config.style_target || null;
        this.data_hub_css_url = config.data_hub_css_url || default_data_hub_css_url || null;
        this.prefill_input_selector = '[data-allow-data-hub-prefill="true"]';
        this.reset_button_selector = '.data-hub-reset-form';
        this.prefill_all_forms = config.prefill_all_forms ?? true;
        this.block_page_on_user_data_load = config.block_page_on_user_data_load ?? true;
        this.prefill_timeout = config.prefill_timeout || 10000;
        this.has_hit_prefill_timeout = false;
        this.get_iterable_user_data_via_hmac_key = config.get_iterable_user_data_via_hmac_key ?? true;
        this.callback_function_after_init = callback_function_after_init;

        this.is_data_hub_form_submission_in_progress = false;
        this.is_initialized = false;

        init_analytics_bootstrap(this, config);

        // Inject theme styles. Resolution: explicit config.theme > organization_metadata.theme_json > build default.
        // organization_metadata is already merged synchronously above (config + window.data_hub_organization_metadata),
        // so server-injected metadata themes apply on first paint with no flash. If organization_metadata is fetched
        // asynchronously and its theme_json arrives later, we re-inject below in start_data_hub_init —
        // config.theme is retained so explicit caller-passed colors keep winning over the async theme_json.
        this.config_theme = (config.theme && typeof config.theme === 'object') ? config.theme : {};
        this.theme = defaultsDeep(
            {},
            this.config_theme,
            this.organization_metadata?.theme_json || {},
            default_theme
        );
        if (this.data_hub_css_url && config.inject_data_hub_css !== false) {
            inject_data_hub_css_link(
                this.style_target || (typeof document !== 'undefined' ? document.head : null),
                {
                    data_hub_css_url: this.data_hub_css_url,
                    error_function: this.error_function
                }
            );
        }
        if (this.style_target) {
            inject_styles(this.theme, this.style_target);
        }

        // Begin initialization
        this.start_data_hub_init();
    }

    callDataHubDispatchEventThenCallbackInit() {
        this.is_initialized = true;

        // Ensure the instance is accessible globally so late consumers can find it
        if (typeof window !== 'undefined' && !window.data_hub) {
            window.data_hub = this;
        }

        document.dispatchEvent(new CustomEvent('data-hub-init', {
            detail: {
                data_hub_instance: this
            },
            bubbles: true,
            composed: true
        }));
        this.callback_function_after_init(this);
        this.load_full_analytics_after_init();
    }

    load_full_analytics_after_init() {
        // this.web_settings is set synchronously by init_analytics_bootstrap during the
        // constructor. This is mutated in place.
        if (!this.web_settings?.enabled) {
            return null;
        }
        if (this.analytics_init_promise) {
            return this.analytics_init_promise;
        }
        this.analytics_init_promise = import(
            /* webpackChunkName: "data_hub_web_analytics" */
            '../analytics/index'
        ).then((analytics_module) => {
            if (typeof analytics_module?.init_analytics === 'function') {
                return analytics_module.init_analytics(this);
            }
            return null;
        }).catch((error) => {
            this.error_function?.('Error loading Data Hub web analytics: ' + error, null);
            return null;
        });
        return this.analytics_init_promise;
    }

    async start_data_hub_init() {
        if (this.prefill_all_forms) {
            add_form_input_loading_state(this.target || document, this.prefill_input_selector);
            add_form_loading_overlay(this.target || document);
        }

        // Maximum time to allow GET user_data. Remove any loading state if hit timeout.
        // But i believe the default is the form shows no loading right now. It will prefill overwrite
        // any non active, non edited marked inputs on response
        if (this.prefill_timeout) {
            setTimeout(() => {
                this.has_hit_prefill_timeout = true;
                remove_form_input_loading_state(this.target || document, this.prefill_input_selector);
                remove_form_loading_overlay(this.target || document);
            }, this.prefill_timeout);
        }

        const skip_organization_metadata_network_fetch = organization_metadata_is_complete_enough_to_skip_fetch(this.organization_metadata);
        const organization_metadata_load_promise = skip_organization_metadata_network_fetch
            ? Promise.resolve(this.organization_metadata)
            : load_organization_public_metadata_from_metadata_module(this);

        // this.forms_domain is always set before start_data_hub_init runs (constructor resolves it
        // from config.data_hub_action_domain / config.forms_domain / build default).
        // Start user_jwt fetch in parallel with organization_metadata — no need to wait for metadata
        // just to know the API domain. user_jwt only reads forms_domain, organization_id, url_params,
        // and user_data — all already set by the constructor.
        const user_data_promise = load_user_data_from_jwt_url_params(this);

        await organization_metadata_load_promise.then((metadata) => {
            if (metadata && typeof metadata === 'object') {
                this.organization_metadata = metadata;
            } else if (metadata) {
                try {
                    this.organization_metadata = JSON.parse(metadata);
                } catch (error) {
                    this.error_function?.('Error parsing organization metadata: ' + error, null);
                    this.organization_metadata = {};
                }
            }
            if (!this.organization_id && this.organization_metadata?.organization_id) {
                this.organization_id = normalize_data_hub_organization_id_string(this.organization_metadata.organization_id);
            }
            if (this.organization_metadata?.actblue_default_token) {
                this.actblue_token = this.organization_metadata.actblue_default_token;
            }
            if (this.organization_metadata?.actblue_default_page_url) {
                this.actblue_default_page_url = this.organization_metadata.actblue_default_page_url;
            }

            // Priority: organization_metadata body -> data_hub_action_domain set in constructor (config or build default) -> hard fail
            this.forms_domain = this.organization_metadata?.data_hub_action_domain || this.organization_metadata?.forms_domain || this.forms_domain;
            if (!this.forms_domain) {
                this.error_function?.('No data_hub_action_domain resolved — cannot reach the API.', 'Configuration error. Please contact support.');
                return;
            }

            // Keep window global in sync (same pattern as window.data_hub_user_data)
            if (typeof window !== 'undefined') {
                window.data_hub_organization_metadata = this.organization_metadata;
            }

            // Re-inject theme if organization_metadata fetched async brought a theme_json.
            // inject_styles is hash-keyed and updates in place — no-op if the theme already
            // matched (e.g. server-injected metadata). Form elements with transition-colors
            // animate the swap smoothly. Priority preserved: config_theme > organization_metadata.theme_json > default.
            if (this.style_target && this.organization_metadata?.theme_json) {
                this.theme = defaultsDeep(
                    {},
                    this.config_theme || {},
                    this.organization_metadata.theme_json,
                    default_theme
                );
                inject_styles(this.theme, this.style_target);
            }
        });

        try {
            // user_jwt was fired in parallel above — await it after organization_metadata processing is done.
            // Both promises run concurrently; DOM is only touched after both resolve.
            await user_data_promise;

            // Always remove loading state when data arrives — don't rely solely on the safety-net timeout.
            // fill_element_with_data skips fields the user has already typed into (data-user-modified).
            remove_form_input_loading_state(this.target || document, this.prefill_input_selector);
            remove_form_loading_overlay(this.target || document);
            if (this.prefill_all_forms) {
                const prefill_root = this.target || document;
                prefill_form(
                    prefill_root,
                    this.user_data,
                    this.prefill_input_selector,
                    this.reset_button_selector,
                    null,
                    false,
                    this
                );
            }
            this.resolve_user_data_loaded();
            this.callDataHubDispatchEventThenCallbackInit();
        } catch (error) {
            // Also remove loading on error — don't leave the form stuck in loading state
            remove_form_input_loading_state(this.target || document, this.prefill_input_selector);
            remove_form_loading_overlay(this.target || document);
            this.reject_user_data_loaded(error);
            this.callDataHubDispatchEventThenCallbackInit();
        }
    }

    // Static helper to convert theme JSON to base64
    static theme_json_to_base64(theme) {
        return run_theme_json_to_base64(theme);
    }

    // Reference wrappers to clean up this file

    /** @see {@link set_page_metadata_defaults_from_metadata_module} */
    set_page_metadata_defaults(config) {
        return set_page_metadata_defaults_from_metadata_module(this, config);
    }

    /** @see {@link load_organization_public_metadata_from_metadata_module} */
    load_organization_public_metadata() {
        return load_organization_public_metadata_from_metadata_module(this);
    }

    /** @see {@link got_cors_user_data_cache_from_cors_user_data_module} */
    got_cors_user_data_cache(payload) {
        return got_cors_user_data_cache_from_cors_user_data_module(this, payload);
    }

    /** @see {@link got_clear_jwt_response_from_forget_user_module} */
    got_clear_jwt_response(payload) {
        return got_clear_jwt_response_from_forget_user_module(this, payload);
    }

    /** @see {@link forget_user_from_forget_user_module} */
    forget_user(options) {
        return forget_user_from_forget_user_module(this, options);
    }
}
